import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

function getPrimaryEmployeeValue(field, item) {
  const placeholder = String(field?.placeholder || "").toLowerCase();
  const fields = item?.fields || {};

  if (placeholder === "u_nome") {
    return fields.first_name || item?.text || "";
  }

  if (placeholder === "u_cognome") {
    return fields.last_name || item?.text || "";
  }

  if (placeholder === "u_cf") {
    return fields.cf || item?.text || "";
  }

  return item?.text || fields.first_name || fields.last_name || fields.cf || "";
}

function getSecondaryEmployeeValues(field, item) {
  const placeholder = String(field?.placeholder || "").toLowerCase();
  const fields = item?.fields || {};

  const firstName = fields.first_name || "";
  const lastName = fields.last_name || "";
  const cf = fields.cf || "";

  if (placeholder === "u_nome") {
    return [lastName, cf].filter(Boolean);
  }

  if (placeholder === "u_cognome") {
    return [firstName, cf].filter(Boolean);
  }

  if (placeholder === "u_cf") {
    return [firstName, lastName].filter(Boolean);
  }

  return [firstName, lastName, cf].filter(Boolean);
}

export default function LetteraEntityLookupField({
  open = false,
  field,
  onClose,
  onPickEntity,
  fetcher,
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 300);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const abortRef = useRef(null);

  const loadResults = async (search, pageArg = 1, append = false) => {
    if (!open) return;
    if (typeof fetcher !== "function") return;
    if (!field?.anagrafica_model || !field?.anagrafica_field) return;

    const filterField =
      field.anagrafica_field.split("__")?.[0] || field.anagrafica_field;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    try {
      const data = await fetcher(
        {
          model: field.anagrafica_model,
          filter_field: filterField,
          q: search,
          page: pageArg,
        },
        controller.signal
      );

      const nextItems = Array.isArray(data?.results) ? data.results : [];
      const more = !!data?.pagination?.more;

      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setHasMore(more);
      setPage(pageArg);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Lookup error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setQuery("");
    setItems([]);
    setPage(1);
    setHasMore(false);
  }, [open, field?.id]);

  useEffect(() => {
    if (!open) return;
    loadResults(debounced, 1, false);
  }, [debounced, open, field?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handlePick = (item) => {
    onPickEntity?.(field?.anagrafica_model, item);
  };

  if (!open || !field) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/30 p-4"
      style={{ zIndex: 999 }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3 bg-indigo-100 rounded-t-xl">
          <h3 className="text-base font-semibold text-indigo-700">
            Seleziona <span className="uppercase font-bold">{field?.nome}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Cerca ${field?.nome?.toLowerCase() || ""}...`}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />

          <div className="mt-4 max-h-[420px] overflow-auto rounded-lg border border-gray-200">
            {loading && !items.length ? (
              <div className="px-4 py-3 text-sm text-gray-500">Caricamento...</div>
            ) : items.length ? (
              <div className="divide-y divide-gray-100">
                {items.map((item, idx) => {
                  const primary = getPrimaryEmployeeValue(field, item);
                  const secondary = getSecondaryEmployeeValues(field, item);

                  return (
                    <button
                      key={`${item.id}-${idx}`}
                      type="button"
                      onClick={() => handlePick(item)}
                      className="block w-full px-4 py-3 text-left hover:bg-indigo-50"
                    >
                      <div className="text-sm font-semibold text-gray-900">
                        {primary || "—"}
                      </div>

                      {secondary.length > 0 && (
                        <div className="mt-1 text-xs text-gray-500">
                          {secondary.join(" · ")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                Nessun risultato
              </div>
            )}
          </div>

          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => loadResults(query, page + 1, true)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {loading ? "Caricamento..." : "Carica altri"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
